/**
 * ApiKeySetup — Manage AI provider API keys stored in the credential vault.
 *
 * Lists existing credentials (provider name + masked key), add/delete forms.
 */
import { useEffect, useState } from "react";
import { Key, Plus, Trash2, Shield, AlertCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { useToast } from "@/components/ui/Toast";
import { useSandboxStore } from "@/stores/useSandboxStore";

/* ------------------------------------------------------------------ */
/*  Provider definitions                                               */
/* ------------------------------------------------------------------ */

const PROVIDERS = [
  { value: "anthropic", label: "Anthropic" },
  { value: "openai", label: "OpenAI" },
  { value: "google", label: "Google" },
  { value: "github", label: "GitHub Copilot" },
  { value: "openrouter", label: "OpenRouter" },
  { value: "aws", label: "AWS Bedrock" },
  { value: "azure", label: "Azure OpenAI" },
  { value: "groq", label: "Groq" },
  { value: "mistral", label: "Mistral" },
] as const;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function ApiKeySetup() {
  const { credentials, fetchCredentials, saveCredential, deleteCredential, error } =
    useSandboxStore();
  const { toast } = useToast();

  const [showForm, setShowForm] = useState(false);
  const [provider, setProvider] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const availableProviders = PROVIDERS.filter(
    (p) => !credentials.some((c) => c.provider === p.value),
  );

  async function handleSave() {
    if (!provider || !apiKey) return;
    setSaving(true);
    try {
      await saveCredential(provider, apiKey);
      toast("API key saved successfully", { variant: "success" });
      setProvider("");
      setApiKey("");
      setShowForm(false);
    } catch {
      toast(error ?? "Failed to save API key", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(prov: string) {
    setDeleting(prov);
    try {
      await deleteCredential(prov);
      toast(`${prov} credential removed`, { variant: "success" });
      setConfirmDelete(null);
    } catch {
      toast(error ?? "Failed to delete credential", { variant: "error" });
    } finally {
      setDeleting(null);
    }
  }

  return (
    <Card data-testid="api-key-setup">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5 text-primary" />
          API Keys
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          API keys are encrypted and stored securely. They are injected into your
          sandbox container at launch time.
        </p>

        {/* Credential list */}
        {credentials.length === 0 ? (
          <EmptyState
            title="No API keys configured"
            description="Add your AI provider API keys to start using the sandbox"
            className="py-6"
          />
        ) : (
          <div className="space-y-2">
            {credentials.map((cred) => {
              const provDef = PROVIDERS.find((p) => p.value === cred.provider);
              return (
                <div
                  key={cred.provider}
                  className="flex items-center gap-3 rounded-lg border border-white/5 bg-surface px-4 py-3"
                  data-testid="credential-row"
                >
                  <Shield className="h-4 w-4 shrink-0 text-emerald-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {provDef?.label ?? cred.provider}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">
                      ••••••••••••
                    </p>
                  </div>
                  <Badge variant="success">Configured</Badge>

                  {confirmDelete === cred.provider ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="danger"
                        size="sm"
                        loading={deleting === cred.provider}
                        onClick={() => handleDelete(cred.provider)}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDelete(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(cred.provider)}
                      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-red-400"
                      aria-label={`Delete ${cred.provider} key`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add form */}
        {showForm ? (
          <div className="rounded-lg border border-white/10 bg-surface p-4 space-y-4">
            <Select
              label="Provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              <option value="">Select provider…</option>
              {availableProviders.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>

            <Input
              label="API Key"
              type="password"
              placeholder="sk-…"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              icon={<Key className="h-4 w-4" />}
            />

            {error && (
              <p className="flex items-center gap-1 text-xs text-danger">
                <AlertCircle className="h-3.5 w-3.5" />
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                loading={saving}
                disabled={!provider || !apiKey}
                size="sm"
              >
                Save Key
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowForm(false);
                  setProvider("");
                  setApiKey("");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowForm(true)}
            disabled={availableProviders.length === 0}
          >
            <Plus className="h-4 w-4" />
            Add API Key
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
